import { Link } from "react-router-dom";
import {ChevronRight, Home as HomeIcon} from 'lucide-react';
import Seo from "@/components/Seo";

/**
 * Élément de fil d'ariane.
 * - `label` : libellé visible.
 * - `href`  : lien interne (omis pour la page courante).
 */
export interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  /** Couleur d'accent du silo : red pour BNSSA, blue pour MNS, gray par défaut. */
  accent?: "red" | "blue" | "gray";
  /** Inclure le pictogramme maison comme premier élément (vers /). Défaut : true. */
  withHome?: boolean;
  /** Classes additionnelles pour le wrapper <nav>. */
  className?: string;
}

const ACCENT_HOVER: Record<"red" | "blue" | "gray", string> = {
  red: "hover:text-red-700",
  blue: "hover:text-blue-700",
  gray: "hover:text-gray-900",
};

/**
 * Classes appliquées à la « page mère » du silo (1er item après Accueil)
 * lorsqu'un accent silo est défini : gras + couleur de l'univers.
 */
const PARENT_SILO_CLASSES: Record<"red" | "blue" | "gray", string> = {
  red: "text-red-700 font-bold hover:text-red-800",
  blue: "text-blue-700 font-bold hover:text-blue-800",
  gray: "",
};

/**
 * Fil d'ariane (breadcrumb) accessible et SEO-friendly.
 *
 * - Rendu visuel : liste ordonnée horizontale avec séparateurs.
 * - Accessibilité : <nav aria-label="Fil d'ariane">, aria-current="page" sur le dernier élément.
 * - SEO : injecte automatiquement un JSON-LD `BreadcrumbList` (schema.org) basé sur les items.
 */
export default function Breadcrumb({
  items,
  accent = "gray",
  withHome = true,
  className = "",
}: BreadcrumbProps) {
  const fullItems: BreadcrumbItem[] = withHome
    ? [{ label: "Accueil", href: "/" }, ...items]
    : items;

  // JSON-LD BreadcrumbList — on n'inclut que les items ayant un href absolu calculable.
  // Les items sans href (page courante) sont quand même listés avec leur position.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: fullItems.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.label,
      ...(item.href
        ? { item: `https://bnssa-mns.fr${item.href}` }
        : {}),
    })),
  };

  const hoverClass = ACCENT_HOVER[accent];
  const parentSiloClass = PARENT_SILO_CLASSES[accent];

  // Index de la « page mère » du silo (BNSSA ou MNS) :
  // - Avec Accueil affiché en tête : c'est l'item d'index 1.
  // - Sans Accueil : c'est l'item d'index 0.
  // On ne met l'emphase silo que si l'accent est red ou blue.
  const parentIndex =
    accent === "red" || accent === "blue" ? (withHome ? 1 : 0) : -1;

  return (
    <>
      <Seo jsonLd={jsonLd} />
      <nav
        aria-label="Fil d'ariane"
        className={`text-sm ${className}`}
      >
        <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-gray-600">
          {fullItems.map((item, index) => {
            const isLast = index === fullItems.length - 1;
            const isHome = withHome && index === 0;
            const isParentSilo = index === parentIndex;
            return (
              <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
                {item.href && !isLast ? (
                  <Link
                    to={item.href}
                    className={`inline-flex items-center gap-1 transition-colors ${
                      isParentSilo
                        ? parentSiloClass
                        : `text-gray-600 ${hoverClass}`
                    }`}
                  >
                    {isHome && <HomeIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                    <span className={isHome ? "sr-only sm:not-sr-only" : ""}>
                      {item.label}
                    </span>
                  </Link>
                ) : (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={`inline-flex items-center gap-1 ${
                      isLast
                        ? "text-gray-900 font-semibold"
                        : isParentSilo
                        ? parentSiloClass
                        : "text-gray-600"
                    } truncate max-w-[60vw] sm:max-w-none`}
                    title={item.label}
                  >
                    {isHome && <HomeIcon className="w-3.5 h-3.5" aria-hidden="true" />}
                    <span className={isHome ? "sr-only sm:not-sr-only" : ""}>
                      {item.label}
                    </span>
                  </span>
                )}
                {!isLast && (
                  <ChevronRight
                    className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
